import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSensory } from '../../contexts/SensoryContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { 
  Sun, Moon, Eye, Type, Menu, X, ChevronDown, 
  User, LogOut, Settings, Heart, Users, ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navLinks = [
  { 
    label: 'About', 
    href: '/about',
    children: [
      { label: 'Our Story', href: '/about#story' },
      { label: 'Board Members', href: '/about#board' },
      { label: 'FAQs', href: '/faqs' }
    ]
  },
  { 
    label: 'Learn', 
    href: '/learn',
    children: [
      { label: 'What is Autism?', href: '/learn#what-is-autism' },
      { label: 'Myths vs Facts', href: '/learn#myths' },
      { label: 'Sensory Differences', href: '/learn#sensory' },
      { label: 'Autistic Strengths', href: '/learn#strengths' }
    ]
  },
  { label: 'Directory', href: '/directory' },
  { label: 'Resources', href: '/resources', notificationKey: 'has_new_resources' },
  { label: 'Events', href: '/events' },
  { label: 'Community', href: '/community', notificationKey: 'has_new_posts' },
  { label: 'Training', href: '/training' }
];

export const Navbar = () => {
  const { isDarkMode, toggleDarkMode, isLowSensory, toggleLowSensory, fontSize, setFontSize } = useSensory();
  const { user, isAdmin, logout, isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const [notifications, setNotifications] = useState({ has_new_posts: false, has_new_resources: false });

  // Fetch notification status when authenticated
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!isAuthenticated) return;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/notifications/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(response.data);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    
    fetchNotifications();
  }, [isAuthenticated]);

  // Mark section as read when visiting
  useEffect(() => {
    const markAsRead = async () => {
      if (!isAuthenticated) return;
      
      const token = localStorage.getItem('token');
      let section = null;
      
      if (location.pathname === '/community' && notifications.has_new_posts) {
        section = 'community';
      } else if (location.pathname === '/resources' && notifications.has_new_resources) {
        section = 'resources';
      }
      
      if (section) {
        try {
          await axios.post(`${API}/notifications/mark-read/${section}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setNotifications(prev => ({
            ...prev,
            [section === 'community' ? 'has_new_posts' : 'has_new_resources']: false
          }));
        } catch (error) {
          console.error('Failed to mark as read:', error);
        }
      }
    };
    
    markAsRead();
  }, [location.pathname, isAuthenticated, notifications.has_new_posts, notifications.has_new_resources]);

  const fontSizes = [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
    { label: 'Extra Large', value: 'xlarge' }
  ];

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/50" data-testid="navbar">
      <div className="container-main">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <img 
              src="/logo.png" 
              alt="Autism Acceptance" 
              className="h-12 md:h-14 w-auto object-contain"
              style={{ maxWidth: '180px' }}
            />
          </Link>

          {/* Desktop Navigation - Only show when authenticated */}
          {isAuthenticated && (
            <nav className="hidden lg:flex items-center gap-0.5">
              {navLinks.map((link) => (
                link.children ? (
                  <DropdownMenu key={link.label}>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-foreground/80 hover:text-foreground hover:bg-primary/10 gap-1 h-8 px-2 text-sm"
                        data-testid={`nav-${link.label.toLowerCase()}`}
                      >
                        {link.label}
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {link.children.map((child) => (
                        <DropdownMenuItem key={child.href} asChild>
                          <Link to={child.href} className="w-full">
                            {child.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link key={link.href} to={link.href} className="relative">
                    <Button 
                      variant="ghost"
                      size="sm"
                      className={`text-foreground/80 hover:text-foreground hover:bg-primary/10 h-8 px-2 text-sm ${
                        location.pathname === link.href ? 'bg-primary/10 text-primary' : ''
                      }`}
                      data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.label}
                      {/* Notification badge */}
                      {link.notificationKey && notifications[link.notificationKey] && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                          <span className="relative inline-flex h-3 w-3 rounded-full bg-primary">
                            <Heart className="h-2 w-2 text-white m-auto" fill="white" />
                          </span>
                        </span>
                      )}
                    </Button>
                  </Link>
                )
              ))}
            </nav>
          )}

          {/* Right side actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Main Website Link */}
            <a 
              href="https://autismacceptance.ky" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden xl:block"
            >
              <Button 
                variant="outline" 
                size="sm" 
                className="border-primary text-primary hover:bg-primary/10 h-8 text-xs px-2"
                data-testid="main-website-btn"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Main Website
              </Button>
            </a>
            
            {/* Accessibility Controls - Compact */}
            <div className="hidden lg:flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                title={isDarkMode ? 'Light mode' : 'Dark mode'}
                data-testid="toggle-dark-mode"
                className="h-8 w-8"
              >
                {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title="Text size"
                    data-testid="text-size-toggle"
                    className="h-8 w-8"
                  >
                    <Type className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {fontSizes.map((size) => (
                    <DropdownMenuItem 
                      key={size.value}
                      onClick={() => setFontSize(size.value)}
                      className={fontSize === size.value ? 'bg-primary/10' : ''}
                    >
                      {size.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button
                variant={isLowSensory ? 'default' : 'ghost'}
                size="icon"
                onClick={toggleLowSensory}
                title={isLowSensory ? 'Standard mode' : 'Low sensory mode'}
                data-testid="toggle-low-sensory"
                className="h-8 w-8"
              >
                <Eye className="h-4 w-4" />
              </Button>
            </div>

            {/* Donate Button */}
            <a 
              href="https://donate.autismacceptance.ky/" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="donate-btn"
            >
              <Button className="bg-[hsl(346,100%,65%)] hover:bg-[hsl(346,100%,55%)] text-white hidden sm:flex h-8 text-xs px-3">
                <Heart className="h-3 w-3 mr-1" />
                Donate
              </Button>
            </a>

            {/* Sign Out Button - Visible when logged in */}
            {isAuthenticated && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={logout}
                className="hidden md:flex h-8 text-xs px-2 text-muted-foreground hover:text-destructive"
                data-testid="logout-btn-visible"
              >
                <LogOut className="h-3 w-3 mr-1" />
                Sign Out
              </Button>
            )}

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="user-menu">
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="w-full flex items-center">
                      <Settings className="h-4 w-4 mr-2" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/favorites" className="w-full flex items-center">
                      <Heart className="h-4 w-4 mr-2" />
                      Favorites
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="w-full flex items-center">
                          <Users className="h-4 w-4 mr-2" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="hidden sm:flex h-8 text-xs" data-testid="login-btn">
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-toggle">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] flex flex-col">
                <div className="flex-shrink-0 flex items-center justify-between mb-4">
                  <img src="/logo.png" alt="Autism Acceptance" className="h-10 object-contain" style={{ maxWidth: '140px' }} />
                </div>
                
                {/* Scrollable content area with scroll indicator */}
                <div className="flex-1 min-h-0 relative">
                  <div className="h-full overflow-y-auto" id="mobile-nav-scroll">
                    {/* Mobile Nav Links - Only show when authenticated */}
                    {isAuthenticated ? (
                      <nav className="flex flex-col gap-1 pb-8">
                        {navLinks.map((link) => (
                          <div key={link.label}>
                            {link.children ? (
                              <div className="space-y-1">
                                <p className="px-3 py-2 text-sm font-semibold text-muted-foreground">
                                  {link.label}
                                </p>
                                {link.children.map((child) => (
                                  <Link
                                    key={child.href}
                                    to={child.href}
                                    onClick={() => setMobileOpen(false)}
                                    className="block px-6 py-2 text-foreground hover:bg-primary/10 rounded-lg"
                                  >
                                    {child.label}
                                  </Link>
                                ))}
                              </div>
                            ) : (
                              <Link
                                to={link.href}
                                onClick={() => setMobileOpen(false)}
                                className={`flex items-center justify-between px-3 py-2 text-foreground hover:bg-primary/10 rounded-lg ${
                                  location.pathname === link.href ? 'bg-primary/10 text-primary' : ''
                                }`}
                              >
                                {link.label}
                                {/* Mobile notification badge */}
                                {link.notificationKey && notifications[link.notificationKey] && (
                                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                                    <Heart className="h-3 w-3 text-white" fill="white" />
                                  </span>
                                )}
                              </Link>
                            )}
                          </div>
                        ))}
                      </nav>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-center px-4 py-8">
                        <p className="text-muted-foreground mb-4">
                          Sign in or register to access all resources and features.
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Scroll indicator gradient */}
                  {isAuthenticated && (
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none flex items-end justify-center pb-1">
                      <ChevronDown className="h-4 w-4 text-muted-foreground animate-bounce" />
                    </div>
                  )}
                </div>

                {/* Fixed bottom section */}
                <div className="flex-shrink-0 border-t pt-4 mt-2">
                  {/* Mobile Accessibility */}
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-muted-foreground mb-2 px-3">
                      Accessibility
                    </p>
                    <div className="flex items-center gap-2 px-3">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={toggleDarkMode}
                        className="h-10 w-10"
                      >
                        {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant={isLowSensory ? 'default' : 'outline'}
                        size="icon"
                        onClick={toggleLowSensory}
                        className="h-10 w-10"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Mobile Auth */}
                  <div className="border-t pt-4">
                    {isAuthenticated ? (
                      <div className="space-y-2 px-3">
                        <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setMobileOpen(false)}>
                            <Button variant="outline" className="w-full mb-2">
                              Admin Panel
                            </Button>
                          </Link>
                        )}
                        <Button variant="outline" className="w-full" onClick={logout}>
                          Sign Out
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2 px-3">
                        <Link to="/login" onClick={() => setMobileOpen(false)}>
                          <Button variant="outline" className="w-full">Sign In</Button>
                        </Link>
                        <Link to="/register" onClick={() => setMobileOpen(false)}>
                          <Button className="w-full btn-primary">Join Hub</Button>
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Mobile Donate */}
                  <div className="mt-4 px-3 pb-4">
                    <a 
                      href="https://donate.autismacceptance.ky/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button className="w-full bg-[hsl(346,100%,65%)] hover:bg-[hsl(346,100%,55%)] text-white">
                        <Heart className="h-4 w-4 mr-2" />
                        Donate
                      </Button>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
